import { useState } from 'react';
import type { ComplianceSummary, EstadoPdb, Organization } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, Pagination, PageHeader, ProgressBar, type Column } from '@/components/ui';
import { HoverCard, HoverRow, InitialsBadge } from '@/components/ui/HoverCard';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { desgloseOrg, hayProgreso, progresoOrg, progresoSite } from '@/lib/progresoPortal';
import { addToast, useAppStore } from '@/store/useAppStore';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';

type OrgRow = Organization & {
  _key: string;
  _sites: number;
  _instr: number;
  _compl: ComplianceSummary | null;
  /** Porcentaje guardado en la base; null si aún no hay. */
  _pct: number | null;
  _pend: number | null;
  _pdb: EstadoPdb | null;
};

/** Barra + porcentaje, igual que la columna de compliance. */
function Barra({ pct }: { pct: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1 }}><ProgressBar pct={pct} /></div>
      <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 34, textAlign: 'right', color: pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
        {pct}%
      </span>
    </div>
  );
}

const sinDato = (lang: string) => (
  <span className="chip" style={{ color: 'var(--text-3)' }} data-tip={lang === 'es' ? 'Aún sin calcular' : 'Not calculated yet'}>—</span>
);

/** Fecha y hora local, para el «quién y cuándo» de PDB. */
const cuando = (iso: string | null, lang: string) =>
  iso ? new Date(iso).toLocaleString(lang === 'es' ? 'es-GT' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** El control PDB: Sí / No, con quién y cuándo en el tooltip. Solo ETC. */
function PdbCell({ r, onChange }: { r: OrgRow; onChange: () => void }) {
  const { lang } = useI18n();
  const [guardando, setGuardando] = useState(false);
  const est = r._pdb ?? { confirmado: false, por: null, en: null };
  const tip = est.en
    ? `${est.confirmado ? (lang === 'es' ? 'Confirmado' : 'Confirmed') : (lang === 'es' ? 'Desmarcado' : 'Unmarked')} · ${est.por ?? ''} · ${cuando(est.en, lang)}`
    : lang === 'es' ? 'Sin confirmar en la Partners Data Base' : 'Not confirmed in the Partners Data Base';

  async function cambiar(e: React.MouseEvent) {
    e.stopPropagation();
    setGuardando(true);
    try {
      await ds.setPdb(r.OrgId, !est.confirmado);
      onChange();
    } catch (err) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se guardó PDB' : 'PDB not saved',
        body: (err as Error).message,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={est.confirmado}
      aria-label={`PDB ${r.OrgName}`}
      data-tip={tip}
      disabled={guardando}
      onClick={cambiar}
      className={'chip ' + (est.confirmado ? 'ok' : '')}
      style={{ cursor: 'pointer', border: '1px solid var(--line-2)', minWidth: 54, justifyContent: 'center', opacity: guardando ? 0.6 : 1 }}
    >
      {est.confirmado ? <>{ic.check} {lang === 'es' ? 'Sí' : 'Yes'}</> : 'No'}
    </button>
  );
}

/** Los sites de una organización, desplegados debajo de su fila, con el
 *  porcentaje de cada uno — y las demás piezas del número de la
 *  organización, para que la suma cuadre a la vista:
 *
 *    ficha + Σ sites + compliance − instructores repetidos = organización */
function ArbolSites({ r }: { r: OrgRow }) {
  const { lang } = useI18n();
  const go = useGo();
  const es = lang === 'es';
  const sites = ds.listSites({ orgId: r.OrgId });
  const d = desgloseOrg(r.OrgId);
  const total = progresoOrg(r.OrgId);
  const pctDe = (h: number, t: number) => (!t || h >= t ? 100 : Math.min(99, Math.floor((h / t) * 100)));

  const Linea = ({ nombre, sub, h, t, pct, onClick, menos }: {
    nombre: React.ReactNode; sub?: string; h: number; t: number; pct?: number | null; onClick?: () => void; menos?: boolean;
  }) => (
    <div
      onClick={onClick}
      className={onClick ? 'data-row' : undefined}
      style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 110px 90px', gap: 12, alignItems: 'center',
        padding: '7px 14px 7px 44px', cursor: onClick ? 'pointer' : 'default', fontSize: 12.5,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombre}</div>
        {sub ? <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{sub}</div> : null}
      </div>
      <div>{pct == null ? null : <Barra pct={pct} />}</div>
      <div className="mono" style={{ textAlign: 'right', color: 'var(--text-2)' }}>
        {menos ? '− ' : ''}{h} {es ? 'de' : 'of'} {t}
      </div>
      <div className="mono" style={{ textAlign: 'right', fontWeight: 700, color: t - h ? 'var(--warn)' : 'var(--ok)' }}>
        {menos ? '− ' : ''}{t - h} {es ? 'pend.' : 'pend.'}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '6px 0 10px' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 110px 90px', gap: 12,
        padding: '4px 14px 6px 44px', fontSize: 10, fontWeight: 800, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '.05em',
      }}>
        <span>{es ? `Sites de la organización (${sites.length})` : `Organization sites (${sites.length})`}</span>
        <span>{es ? '% Progreso' : '% Progress'}</span>
        <span style={{ textAlign: 'right' }}>{es ? 'Datos' : 'Data'}</span>
        <span style={{ textAlign: 'right' }}>{es ? 'Pendientes' : 'Pending'}</span>
      </div>

      {sites.length === 0 ? (
        <div style={{ padding: '6px 14px 6px 44px', color: 'var(--text-3)', fontSize: 12.5 }}>
          {es ? 'Esta organización no tiene sites.' : 'This organization has no sites.'}
        </div>
      ) : sites.map((s) => {
        const p = progresoSite(s.SiteId);
        const hay = hayProgreso('site', s.SiteId);
        return (
          <Linea
            key={s.SiteId}
            nombre={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{sized(ic.site, 13)}</span>{s.SiteName}
            </span>}
            sub={s.SiteId}
            h={p.hechos} t={p.total} pct={hay ? p.pct : null}
            onClick={() => go('site', { siteId: s.SiteId })}
          />
        );
      })}

      {d ? (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--line-2)' }}>
          <Linea nombre={es ? 'Datos de la organización (ficha)' : 'Organization record'}
            h={d.ficha.hechos} t={d.ficha.total} pct={pctDe(d.ficha.hechos, d.ficha.total)} />
          {d.compliance.total ? (
            <Linea nombre={es ? 'Documentos de compliance' : 'Compliance documents'}
              h={d.compliance.hechos} t={d.compliance.total} pct={pctDe(d.compliance.hechos, d.compliance.total)} />
          ) : null}
          {d.repetidos.total ? (
            <Linea
              nombre={es ? 'Instructores en más de un site (cuentan una sola vez)' : 'Instructors in more than one site (counted once)'}
              h={d.repetidos.hechos} t={d.repetidos.total} menos
            />
          ) : null}
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 110px 90px', gap: 12, alignItems: 'center',
            padding: '8px 14px 4px 44px', fontSize: 12.5, fontWeight: 800, borderTop: '1px solid var(--line)', marginTop: 4,
          }}>
            <span>{es ? 'Total de la organización' : 'Organization total'}</span>
            <Barra pct={total.pct} />
            <span className="mono" style={{ textAlign: 'right' }}>{total.hechos} {es ? 'de' : 'of'} {total.total}</span>
            <span className="mono" style={{ textAlign: 'right', color: total.total - total.hechos ? 'var(--warn)' : 'var(--ok)' }}>
              {total.total - total.hechos} {es ? 'pend.' : 'pend.'}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Compact summary of the most relevant organization data, on hover. */
function OrgNameCell({ r }: { r: OrgRow }) {
  const { lang, t } = useI18n();
  const country = COUNTRIES.find((c) => c.code === r.OrgCountry)?.[lang === 'es' ? 'name_es' : 'name_en'] || r.OrgCountry;
  // el porcentaje del socio: su ficha, sus sites, sus instructores y sus
  // documentos de compliance, no solo los campos de esta tabla
  const c = progresoOrg(r.OrgId);
  const addr = [r.RegisteredAddress1, r.RegisteredCity, r.RegisteredStateProvince].filter(Boolean).join(', ');

  return (
    <HoverCard
      trigger={
        <>
          <div style={{ fontWeight: 600 }}>{r.OrgName}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.OrgId}</div>
        </>
      }
    >
      {() => (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <InitialsBadge name={r.OrgName} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-ink)', letterSpacing: '-0.01em' }}>{r.OrgName}</div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{r.OrgId}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <HoverRow label={lang === 'es' ? 'País' : 'Country'}>{country}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Dirección' : 'Address'}>{addr || '—'}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Ingreso' : 'Joined'}>{r.YearJoined}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Sitio web' : 'Website'} bad={!r.OrgWebAddress}>
              {r.OrgWebAddress || (lang === 'es' ? 'Sin dato' : 'Missing')}
            </HoverRow>
            <HoverRow label="Sites">{r._sites}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Instructores' : 'Instructors'}>{r._instr}</HoverRow>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0 }}>{t('common.completeness')}</span>
            <div style={{ flex: 1 }}><ProgressBar pct={c.pct} /></div>
            <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{c.pct}%</span>
          </div>
        </>
      )}
    </HoverCard>
  );
}

export interface OrgListProps {
  /** Restricts the list to a partner manager's own organizations. */
  orgIds?: string[];
  title?: string;
  sub?: string;
}

export function OrgList({ orgIds, title, sub }: OrgListProps) {
  const { lang, t } = useI18n();
  const go = useGo();
  const role = useAppStore((st) => st.role);
  // Las columnas nuevas (progreso, pendientes, PDB y el árbol) son de ETC.
  // Para el resto de cuentas la tabla sigue como estaba.
  const esEtc = role === 'ETC_ADMIN';
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const [, setPdbVersion] = useState(0);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const table = useTableState(12);

  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
  };

  let all = ds.listOrgs({ q });
  if (orgIds) all = all.filter((o) => orgIds.includes(o.OrgId));

  const countryOptions = Array.from(new Set(all.map((o) => o.OrgCountry).filter(Boolean)))
    .map((c) => ({ code: c, name: countryName(c) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  if (country) all = all.filter((o) => o.OrgCountry === country);

  // enrich every match first, so sorting spans all pages
  const enriched: OrgRow[] = all.map((o) => ({
    ...o,
    _key: o.OrgId,
    _sites: ds.listSites({ orgId: o.OrgId }).length,
    _instr: ds.listAssignments({ orgId: o.OrgId }).length,
    _compl: ds.getCompliance(o.OrgId),
    ...(() => {
      if (!hayProgreso('organization', o.OrgId)) return { _pct: null, _pend: null };
      const p = progresoOrg(o.OrgId);
      return { _pct: p.pct, _pend: p.total - p.hechos };
    })(),
    _pdb: ds.getPdb(o.OrgId),
  }));

  const sortValues: SortMap<OrgRow> = {
    OrgName: { type: 'text', get: (r) => r.OrgName },
    country: { type: 'text', get: (r) => countryName(r.OrgCountry) },
    _sites: { type: 'number', get: (r) => r._sites },
    _instr: { type: 'number', get: (r) => r._instr },
    YearJoined: { type: 'number', get: (r) => Number(r.YearJoined) || 0 },
    web: { type: 'number', get: (r) => (r.OrgWebAddress ? 1 : 0) },
    compliance: { type: 'number', get: (r) => (r._compl ? r._compl.pct : -1) },
    progreso: { type: 'number', get: (r) => r._pct ?? -1 },
    pendientes: { type: 'number', get: (r) => r._pend ?? -1 },
    pdb: { type: 'number', get: (r) => (r._pdb?.confirmado ? 1 : 0) },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const colNombre: Column<OrgRow> = { key: 'OrgName', label: lang === 'es' ? 'Organización' : 'Organization', sortable: true, render: (r) => <OrgNameCell r={r} /> };
  const colPais: Column<OrgRow> = { key: 'country', label: lang === 'es' ? 'País' : 'Country', sortable: true, render: (r) => countryName(r.OrgCountry) };
  const colSites: Column<OrgRow> = { key: '_sites', label: 'Sites', align: 'right', mono: true, sortable: true };
  const colCompliance: Column<OrgRow> =
    {
      key: 'compliance',
      label: 'Compliance',
      sortable: true,
      render: (r) =>
        !r._compl ? (
          <span className="chip" style={{ color: 'var(--text-3)' }} data-tip={lang === 'es' ? 'Sin documentos de compliance' : 'No compliance documents'}>
            —
          </span>
        ) : (
          <div
            data-tip={
              lang === 'es'
                ? `${r._compl.count} documento${r._compl.count === 1 ? '' : 's'} · ${r._compl.verified} verificado${r._compl.verified === 1 ? '' : 's'}`
                : `${r._compl.count} document${r._compl.count === 1 ? '' : 's'} · ${r._compl.verified} verified`
            }
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}
          >
            <div style={{ flex: 1 }}><ProgressBar pct={r._compl.pct} /></div>
            <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 34, textAlign: 'right', color: r._compl.pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
              {r._compl.pct}%
            </span>
          </div>
        ),
    };
  const colIr: Column<OrgRow> = { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> };

  const alternar = (orgId: string) => setAbiertas((prev) => {
    const n = new Set(prev);
    if (n.has(orgId)) n.delete(orgId); else n.add(orgId);
    return n;
  });

  const columns: Column<OrgRow>[] = esEtc
    ? [
      {
        key: 'tree',
        label: '',
        render: (r) => (
          <button
            type="button"
            className="btn ghost"
            aria-expanded={abiertas.has(r.OrgId)}
            aria-label={(lang === 'es' ? 'Ver sites de ' : 'Show sites of ') + r.OrgName}
            onClick={(e) => { e.stopPropagation(); alternar(r.OrgId); }}
            style={{ padding: 4, minWidth: 0, transition: 'transform .15s', transform: abiertas.has(r.OrgId) ? 'rotate(90deg)' : 'none' }}
          >
            {ic.chevR}
          </button>
        ),
      },
      colNombre,
      colPais,
      colSites,
      colCompliance,
      {
        key: 'progreso',
        label: lang === 'es' ? '% Progreso' : '% Progress',
        sortable: true,
        render: (r) => (r._pct == null ? sinDato(lang) : <Barra pct={r._pct} />),
      },
      {
        key: 'pendientes',
        label: lang === 'es' ? 'Pendientes' : 'Pending',
        align: 'right',
        sortable: true,
        render: (r) =>
          r._pend == null ? sinDato(lang) : (
            <span className="mono" style={{ fontWeight: 700, color: r._pend ? 'var(--warn)' : 'var(--ok)' }}>{r._pend}</span>
          ),
      },
      {
        key: 'pdb',
        label: <span data-tip="Partners Data Base">PDB</span>,
        align: 'center',
        sortable: true,
        render: (r) => <PdbCell r={r} onChange={() => setPdbVersion((v) => v + 1)} />,
      },
      colIr,
    ]
    : [
      colNombre,
      colPais,
      colSites,
      { key: '_instr', label: lang === 'es' ? 'Instr.' : 'Instr.', align: 'right', mono: true, sortable: true },
      { key: 'YearJoined', label: lang === 'es' ? 'Año' : 'Year', align: 'right', mono: true, sortable: true },
      {
        key: 'web',
        label: 'Web',
        sortable: true,
        render: (r) => (r.OrgWebAddress ? <span className="chip ok">{ic.check} OK</span> : <span className="chip warn">—</span>),
      },
      colCompliance,
      colIr,
    ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={title || t('nav.orgs')}
        sub={sub != null ? sub : lang === 'es' ? `${all.length} organizaciones en la base` : `${all.length} organizations in the database`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                table.setPage(1);
              }}
              aria-label={lang === 'es' ? 'Filtrar por país' : 'Filter by country'}
              style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--surface)', maxWidth: 180 }}
            >
              <option value="">{lang === 'es' ? 'Todos los países' : 'All countries'}</option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
              <input
                placeholder={t('common.search')}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  table.setPage(1);
                }}
                style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, width: 240 }}
              />
            </div>
          </div>
        }
      />
      <DataTable
        columns={columns}
        rows={rows}
        sort={table.sort}
        onSort={table.onSort}
        stickyHeader
        onRowClick={(o) => go('org', { orgId: o.OrgId })}
        expanded={esEtc ? (r) => (abiertas.has(r.OrgId) ? <ArbolSites r={r} /> : null) : undefined}
      />
      <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={all.length} per={table.per} onPer={table.onPer} perOptions={[12, 25, 50, 100]} />
    </div>
  );
}
